/**
 * LeadAddToOpenHouseModal — add a lead to an existing open house appointment.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { usePropertyTimezone } from '../../../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import { fetchShowingsForListing, addAttendee } from '../../../../lib/leasing.api';
import type { LeadDetail, ShowingAppointment } from '../shared';

export interface LeadAddToOpenHouseModalProps {
  lead: LeadDetail;
  onClose: () => void;
  onSuccess: () => void;
}

export function LeadAddToOpenHouseModal({ lead, onClose, onSuccess }: LeadAddToOpenHouseModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const propertyTimezone = usePropertyTimezone();

  const { data: showingsData, isLoading, error: loadError, refetch: refetchOpenHouses } = useQuery({
    queryKey: ['listing', lead.listingId, 'showings-open-house'],
    queryFn: () => fetchShowingsForListing(lead.listingId),
  });

  // Escape key + focus trap
  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus first focusable element on open
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE);
      firstFocusable?.focus();
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Only show upcoming open house appointments that aren't cancelled
  const openHouses = (showingsData?.items ?? []).filter(
    (a) => a.kind === 'open_house' && a.outcome !== 'cancelled' && new Date(a.scheduledFor) > new Date()
  );

  const handleAdd = async (appt: ShowingAppointment) => {
    setError(null);
    setIsSubmitting(true);
    try {
      await addAttendee(lead.listingId, appt.id, { leadId: lead.id });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to open house');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="lead-add-to-open-house-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-add-to-open-house-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 id="lead-add-to-open-house-title" className="text-lg font-semibold mb-4">
          Add to Open House
        </h2>

        {isLoading && <Skeleton className="h-20 w-full" />}

        {loadError && (
          <div
            data-testid="lead-add-open-house-load-error"
            className="mb-3 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            role="alert"
          >
            <p>Failed to load open house appointments. Please try again.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="lead-add-open-house-retry"
              onClick={() => void refetchOpenHouses()}
            >
              Retry
            </Button>
          </div>
        )}

        {error && (
          <div
            data-testid="lead-add-open-house-error"
            className="mb-3 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {!isLoading && !loadError && openHouses.length === 0 && (
          <p className="text-sm text-muted-foreground italic mb-4">
            No upcoming open house appointments found. Schedule one from the Listing detail page first.
          </p>
        )}

        {openHouses.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="open-house-picker-list">
            {openHouses.map((appt) => {
              const alreadyAdded = appt.attendees.some((a) => a.leadId === lead.id);
              return (
                <div
                  key={appt.id}
                  data-testid={`open-house-option-${appt.id}`}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium">{formatTimestamp(asIsoDateTimeString(appt.scheduledFor), propertyTimezone)}</p>
                    <p className="text-xs text-muted-foreground">
                      {appt.attendees.length} attendee{appt.attendees.length !== 1 ? 's' : ''}
                      {appt.maxAttendees != null && ` / ${appt.maxAttendees} max`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`open-house-add-btn-${appt.id}`}
                    disabled={isSubmitting || alreadyAdded}
                    onClick={() => void handleAdd(appt)}
                  >
                    {alreadyAdded ? 'Added' : 'Add'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          data-testid="lead-add-open-house-close"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full"
        >
          Close
        </Button>
      </div>
    </div>
  );
}
